import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useErrand, ErrandStop, UpdateStopStatusData } from '../contexts/ErrandContext';
import StashitLogo from '../components/Logo/StashitLogo';
import { API_ENDPOINTS } from '../config/api';
import {
  DashboardStatsSkeleton,
  AnalyticsSkeleton,
  StatusBadge,
  LoadingSpinner,
  ProgressiveLoader
} from '../components/UI/LoadingSkeleton';
import ErrorBoundary from '../components/UI/ErrorBoundary';
import PullToRefresh from '../components/UI/PullToRefresh';
import { supabase } from '../lib/supabase';
import ExtraChargesPanel from '../components/ExtraChargesPanel';
import RatingModal from '../components/Errand/RatingModal';

interface ErrandSummary {
  id: string;
  title: string;
  description: string;
  status: string; // Made more flexible to match actual errand statuses
  payment_method: string; // Made more flexible to match actual payment methods
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded';
  total_cost?: number;
  created_at: string;
  customer_name?: string;
  pickup_location: string;
  dropoff_location?: string;
  courier_company?: string;
  is_multi_stop?: boolean;
  completed_at?: string;
  updated_at?: string;
  estimated_value?: number;
  driver_earnings?: number;
  user_id?: string;
  profiles?: {
    full_name: string;
  };
}

interface EarningsSummary {
  total_earnings: number;
  monthly_earnings: number;
  pending_commission: number;
  completed_errands: number;
  total_errands: number;
  commission_rate: number;
}

interface DriverStats {
  total_distance: number;
  average_rating: number;
  completion_rate: number;
  response_time: number; // in minutes
}

interface DriverAnalytics {
  driver: {
    id: string;
    name: string;
    rating: number;
    total_distance: number;
    response_time_avg: number;
  };
  earnings: {
    total: number;
    net: number;
    commission: number;
    today: number;
    period: string;
  };
  performance: {
    total_errands: number;
    completed_errands: number;
    completion_rate: number;
    average_completion_time: number;
    active_errands: number;
  };
  errands_by_status: Record<string, number>;
  earnings_by_month: Array<{
    month: string;
    earnings: number;
    commission: number;
    net: number;
    errands: number;
  }>;
  recent_activity: Array<{
    id: string;
    status: string;
    amount: number;
    commission: number;
    created_at: string;
    pickup_location: string;
    dropoff_location?: string;
  }>;
}

interface UserProfile {
  id: string;
  isDriver?: boolean;
  role?: string;
  // Add other profile properties as needed
}

const DriverDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isVerified, setIsVerified] = useState(false);
  const { fetchDriverErrands, driverErrands, errandStops, updateStopStatus } = useErrand();
  const [earningsSummary, setEarningsSummary] = useState<EarningsSummary>({
    total_earnings: 0,
    monthly_earnings: 0,
    pending_commission: 0,
    completed_errands: 0,
    total_errands: 0,
    commission_rate: 0.15 // 15% commission rate
  });
  const [driverStats, setDriverStats] = useState<DriverStats>({
    total_distance: 0,
    average_rating: 0,
    completion_rate: 0,
    response_time: 0
  });
  const [driverAnalytics, setDriverAnalytics] = useState<DriverAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'errands' | 'earnings' | 'analytics' | 'charges' | 'commission'>('overview');
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(10);
  const [updatingStopId, setUpdatingStopId] = useState<string | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedErrandForRating, setSelectedErrandForRating] = useState<ErrandSummary | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [kycNotification, setKycNotification] = useState<{
    show: boolean;
    message: string;
    actionText?: string;
    onAction?: () => void;
  }>({ show: false, message: '' });
  const [commissionForm, setCommissionForm] = useState({
    payment_method: '',
    amount: 0,
    account_details: ''
  });
  const [commissionHistory, setCommissionHistory] = useState<any[]>([]);
  const [commissionLoading, setCommissionLoading] = useState(false);

  // Request throttling and caching state
  const [lastRequestTime, setLastRequestTime] = useState<{[key: string]: number}>({});
  const [cachedData, setCachedData] = useState<{[key: string]: {data: any, timestamp: number}}>({});
  const [isRateLimited, setIsRateLimited] = useState<{[key: string]: boolean}>({});
  const [lastDataRefresh, setLastDataRefresh] = useState<number>(0);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Rate limiting utility with exponential backoff and caching
  const throttledFetch = useCallback(async (
    endpoint: string,
    options: RequestInit = {},
    throttleKey: string,
    minInterval: number = 20000, // 20 seconds minimum between requests (further increased)
    useCache: boolean = true
  ) => {
    const now = Date.now();

    // Check cache first
    if (useCache && cachedData[throttleKey]) {
      const cacheEntry = cachedData[throttleKey];
      if (now - cacheEntry.timestamp < CACHE_DURATION) {
        console.log(`Using cached data for ${throttleKey}`);
        return new Response(JSON.stringify(cacheEntry.data), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        // Remove expired cache
        setCachedData(prev => {
          const newCache = { ...prev };
          delete newCache[throttleKey];
          return newCache;
        });
      }
    }

    const lastRequest = lastRequestTime[throttleKey] || 0;
    const timeSinceLastRequest = now - lastRequest;

    if (timeSinceLastRequest < minInterval) {
      console.log(`Throttling request to ${endpoint}, waiting ${minInterval - timeSinceLastRequest}ms`);
      await new Promise(resolve => setTimeout(resolve, minInterval - timeSinceLastRequest));
    }

    setLastRequestTime(prev => ({ ...prev, [throttleKey]: Date.now() }));

    try {
      const response = await fetch(endpoint, options);

      if (response.status === 429) {
        setIsRateLimited(prev => ({ ...prev, [throttleKey]: true }));
        console.warn(`Rate limited for ${throttleKey}, implementing backoff`);
        // Set longer backoff for rate limited endpoints
        setLastRequestTime(prev => ({ ...prev, [throttleKey]: Date.now() + 60000 })); // 60 second backoff
        throw new Error('Too many requests. Please wait a moment and try again.');
      }

      setIsRateLimited(prev => ({ ...prev, [throttleKey]: false }));

      // Cache successful responses
      if (useCache && response.ok && response.headers.get('content-type')?.includes('application/json')) {
        try {
          const responseClone = response.clone();
          const data = await responseClone.json();
          setCachedData(prev => ({
            ...prev,
            [throttleKey]: { data, timestamp: now }
          }));
        } catch (cacheError) {
          // Ignore cache errors
        }
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message.includes('Too many requests')) {
        throw error;
      }
      throw error;
    }
  }, [lastRequestTime, cachedData]);

  // Function declarations - moved to top to avoid hoisting issues
  const getUserProfile = useCallback(async () => {
    if (userProfile) {
      return userProfile;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const profileData = await response.json();
        setUserProfile(profileData.profile);
        return profileData.profile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }, [userProfile]);

  const calculateEarningsFromErrands = useCallback(async (driverId: string) => {
    try {
      const { data: errands, error } = await supabase
        .from('errands')
        .select('id, total_cost, payment_status, completed_at')
        .eq('driver_id', driverId)
        .eq('status', 'completed'); // Only consider completed errands

      if (error) {
        console.error('Error fetching errands for earnings calculation:', error);
        return;
      }

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      let totalEarnings = 0;
      let monthlyEarnings = 0;
      let completedErrandsCount = 0;

      errands.forEach(errand => {
        if (errand.payment_status === 'completed' && errand.total_cost) {
          const driverShare = errand.total_cost * (1 - earningsSummary.commission_rate); // Use existing commission rate
          totalEarnings += driverShare;
          completedErrandsCount++;

          if (errand.completed_at) {
            const completedDate = new Date(errand.completed_at);
            if (completedDate >= startOfMonth) {
              monthlyEarnings += driverShare;
            }
          }
        }
      });

      setEarningsSummary(prevSummary => ({
        ...prevSummary,
        total_earnings: totalEarnings,
        monthly_earnings: monthlyEarnings,
        completed_errands: completedErrandsCount,
        total_errands: errands.length, // Total completed errands fetched for this driver
        pending_commission: 0 // If calculated from errands, assume no pending commission from this source
      }));

    } catch (error) {
      console.error('Failed to calculate earnings from errands:', error);
    }
  }, [setEarningsSummary, earningsSummary.commission_rate]);

  const fetchEarningsSummary = useCallback(async () => {
    try {
      // Get user ID
      const profileData = await getUserProfile();
      if (!profileData) {
        console.log('No profile available for earnings fetch');
        return;
      }

      const driverId = profileData.id;

      // Try API endpoint first, fallback to direct database query
      try {
        const token = localStorage.getItem('token');
        const earningsResponse = await fetch(`${API_ENDPOINTS.BASE}/api/earnings/summary`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (earningsResponse.ok) {
          const contentType = earningsResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await earningsResponse.json();
            const summary = data.summary || data;
            setEarningsSummary({
              total_earnings: typeof summary.total_earnings === 'number' ? summary.total_earnings : parseFloat(String(summary.total_earnings || summary.totalEarnings || 0)),
              monthly_earnings: typeof summary.monthly_earnings === 'number' ? summary.monthly_earnings : parseFloat(String(summary.monthly_earnings || summary.monthlyEarnings || 0)),
              pending_commission: typeof summary.commission_due === 'number' ? summary.commission_due : parseFloat(String(summary.commission_due || summary.pendingCommission || 0)),
              completed_errands: typeof summary.completed_errands === 'number' ? summary.completed_errands : parseInt(String(summary.completed_errands || summary.completedErrands || 0), 10),
              total_errands: typeof summary.total_errands === 'number' ? summary.total_errands : parseInt(String(summary.total_errands || summary.totalErrands || 0), 10),
              commission_rate: 0.15
            });
            return;
          }
        }
      } catch (apiError) {
        console.log('API endpoint failed, querying database directly...', apiError);
      }

      // Fallback: Query driver_earnings table directly
      const { data: earningsData, error: earningsError } = await supabase
        .from('driver_earnings')
        .select('driver_share, platform_commission, commission_status, commission_paid_amount, total_collected, created_at')
        .eq('driver_id', driverId);

      if (earningsError) {
        console.error('Error fetching driver earnings:', earningsError);
        // Also try to calculate from errands
        await calculateEarningsFromErrands(driverId);
        return;
      }

      // Calculate summary from driver_earnings
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Total earnings: sum of driver_share where commission_status = 'paid'
      const totalEarnings = earningsData?.reduce((sum, e) => {
        if (e.commission_status === 'paid') {
          const driverShare = typeof e.driver_share === 'number' ? e.driver_share : parseFloat(String(e.driver_share || 0));
          return sum + driverShare;
        }
        return sum;
      }, 0) || 0;

      // Monthly earnings: sum of driver_share where commission_status = 'paid' and created_at >= start of month
      const monthlyEarnings = earningsData?.filter(e => {
        const earningDate = new Date(e.created_at);
        return earningDate >= startOfMonth && e.commission_status === 'paid';
      }).reduce((sum, e) => {
        const driverShare = typeof e.driver_share === 'number' ? e.driver_share : parseFloat(String(e.driver_share || 0));
        return sum + driverShare;
      }, 0) || 0;

      // Pending commission: sum of platform_commission where commission_status = 'due'
      const pendingCommission = earningsData?.filter(e => e.commission_status === 'due')
        .reduce((sum, e) => {
          const platformCommission = typeof e.platform_commission === 'number' ? e.platform_commission : parseFloat(String(e.platform_commission || 0));
          return sum + platformCommission;
        }, 0) || 0;

      // Get errand counts from driver_earnings
      const completedErrands = earningsData?.filter(e => e.commission_status === 'paid').length || 0;
      const totalErrands = earningsData?.length || 0;

      setEarningsSummary({
        total_earnings: totalEarnings,
        monthly_earnings: monthlyEarnings,
        pending_commission: pendingCommission,
        completed_errands: completedErrands,
        total_errands: totalErrands,
        commission_rate: 0.15
      });
    } catch (error) {
      console.error('Error fetching earnings summary:', error);
      // Try to calculate from errands as last resort
      const profileData = await getUserProfile();
      if (profileData) {
        try {
          await calculateEarningsFromErrands(profileData.id);
        } catch (e) {
          console.error('Failed to calculate earnings from errands:', e);
        }
      }
    }
  }, [getUserProfile, calculateEarningsFromErrands]);

  const fetchDriverStats = useCallback(async () => {
    try {
      // Calculate basic stats from errands
      const completedErrands = driverErrands.filter(e => e.status === 'completed');
      const totalErrands = driverErrands.length;

      setDriverStats({
        total_distance: completedErrands.length * 5.2, // Average distance per errand
        average_rating: 4.8, // Placeholder - would come from ratings system
        completion_rate: totalErrands > 0 ? (completedErrands.length / totalErrands) * 100 : 0,
        response_time: 12 // Placeholder - would be calculated from actual response times
      });
    } catch (error) {
      console.error('Error calculating driver stats:', error);
    }
  }, [driverErrands]);

  const fetchDriverAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      setError(null); // Clear any previous errors

      const token = localStorage.getItem('token');
      if (!token || !user) {
        console.log('No token or user, skipping analytics fetch');
        return;
      }

      // Get user profile to get driver ID
      const profileData = await getUserProfile();
      if (!profileData) {
        console.log('No profile data available');
        return;
      }

      const driverId = profileData.id;
      console.log('Fetching analytics for driver:', driverId);

      // Use throttled fetch for analytics to prevent 429 errors
      const analyticsResponse = await throttledFetch(
        `${API_ENDPOINTS.BASE}/api/analytics/driver/${driverId}?period=month`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: AbortSignal.timeout(15000) // 15 second timeout
        },
        'analytics-fetch',
        120000, // 2 minutes minimum between analytics fetches
        true
      );

      console.log('Analytics response status:', analyticsResponse.status);

      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        console.log('Analytics data received:', analyticsData);
        setDriverAnalytics(analyticsData);
      } else {
        console.error('Analytics API error:', analyticsResponse.status, analyticsResponse.statusText);
        // Set error state for display
        setError(`Failed to load analytics: ${analyticsResponse.status}`);
      }
    } catch (error) {
      console.error('Error fetching driver analytics:', error);
      setError('Failed to load analytics data. Please check your connection and try again.');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [user, getUserProfile, throttledFetch]);

  const fetchDriverData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch driver errands with location filter if available
      if (userLocation) {
        await fetchDriverErrands(userLocation.latitude, userLocation.longitude, radiusKm);
      } else {
        await fetchDriverErrands();
      }

      // Fetch earnings summary
      await fetchEarningsSummary();

      // Fetch driver stats
      await fetchDriverStats();

      // Fetch analytics data
      await fetchDriverAnalytics();

    } catch (error) {
      console.error('Error fetching driver data:', error);
      setError('Failed to fetch driver data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [userLocation, fetchDriverErrands, fetchEarningsSummary, fetchDriverStats, fetchDriverAnalytics, radiusKm]);

  const checkVerificationStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const profileData = await response.json();
        setIsVerified(profileData.profile?.is_verified || false);
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  }, []);

  const handleUpdateStopStatus = async (errandId: string, stopId: string, status: 'arrived' | 'completed') => {
    setUpdatingStopId(stopId);
    try {
      await updateStopStatus({ errandId, stopId, status });
    } catch (error) {
      console.error('Failed to update stop status:', error);
    } finally {
      setUpdatingStopId(null);
    }
  };

  const handleCommissionPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commissionForm.payment_method || !commissionForm.amount) {
      return;
    }

    setCommissionLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/payments/commission/pay`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(commissionForm)
      });

      if (response.ok) {
        const result = await response.json();
        // Reset form
        setCommissionForm({
          payment_method: '',
          amount: 0,
          account_details: ''
        });
        // Refresh earnings to show updated commission
        await fetchEarningsSummary();
        // Refresh commission history
        await fetchCommissionHistory();
      } else {
        throw new Error('Failed to submit commission payment request');
      }
    } catch (error) {
      console.error('Commission payment error:', error);
    } finally {
      setCommissionLoading(false);
    }
  };

  const fetchCommissionHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/payments/commission/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCommissionHistory(data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching commission history:', error);
    }
  };


  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          // Handle geolocation errors gracefully
          if (error.code === error.PERMISSION_DENIED) {
            console.log('Geolocation permission denied by user');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            console.log('Geolocation position unavailable');
          } else if (error.code === error.TIMEOUT) {
            console.log('Geolocation request timed out');
          } else {
            console.log('Geolocation error:', error.message || error);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    }
  }, []);

  // Check for pending KYC applications when component mounts
  const checkPendingKYC = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !user) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/my-application`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const application = data.application;

        if (application && application.status === 'kyc_required') {
          setKycNotification({
            show: true,
            message: 'Your driver application requires KYC verification. Please upload your documents to complete the process.',
            actionText: 'Upload KYC Documents',
            onAction: () => navigate('/driver/apply')
          });
        } else if (application && application.status === 'pending' && (!application.kyc_documents || application.kyc_documents.length < 3)) {
          setKycNotification({
            show: true,
            message: 'Your driver application is being reviewed. KYC documents are still needed for approval.',
            actionText: 'Complete KYC',
            onAction: () => navigate('/driver/apply')
          });
        }
      }
    } catch (error) {
      console.error('Error checking KYC status:', error);
    }
  }, [user, navigate]);

  useEffect(() => {
    // Allow access if user is a driver (role='driver' or isDriver=true)
    if (user && (user.role === 'driver' || user.isDriver)) {
      fetchDriverData();
      checkVerificationStatus();
      checkPendingKYC();
    } else if (user) {
      // User is logged in but not a driver, redirect to home
      navigate('/');
    }
    // If user is null, wait for auth check
  }, [user, navigate, fetchDriverData, checkVerificationStatus, checkPendingKYC]);

  // Effect for fetching data when tabs become active (no polling to prevent rate limiting)
  useEffect(() => {
    if (activeTab === 'analytics' && user && (user.role === 'driver' || user.isDriver)) {
      // Only fetch once when tab becomes active, not continuously
      if (!driverAnalytics) {
        fetchDriverAnalytics();
      }
    }

    // Fetch commission history when commission tab is active
    if (activeTab === 'commission' && user && (user.role === 'driver' || user.isDriver)) {
      fetchCommissionHistory();
    }
  }, [activeTab, user]); // Removed fetchDriverAnalytics from dependencies to prevent re-fetching

  // Consolidated refresh function with enhanced throttling
  const throttledRefresh = useCallback(async (force = false) => {
    const now = Date.now();

    // For earnings-related refreshes, use even stricter throttling (60 seconds)
    const minInterval = force ? 10000 : 60000; // 10s for forced, 60s for regular

    if (now - lastDataRefresh < minInterval) {
      console.log(`Skipping earnings refresh - too soon since last refresh (${(now - lastDataRefresh)/1000}s ago)`);
      return;
    }

    console.log('Performing throttled earnings refresh...');
    setLastDataRefresh(now);

    try {
      // Only refresh earnings data, not stats to avoid multiple rate limits
      await fetchEarningsSummary();
    } catch (error) {
      console.error('Error during earnings refresh:', error);
    }
  }, [lastDataRefresh, fetchEarningsSummary]);

  // Listen for errand payment marked events and errand completion to refresh earnings and stats
  useEffect(() => {
    const handlePaymentMarked = () => {
      console.log('Errand payment marked, scheduling refresh...');
      throttledRefresh(true); // Force refresh for payment events
    };

    const handleErrandCompleted = () => {
      console.log('Errand completed, scheduling refresh...');
      throttledRefresh(true); // Force refresh for completion events
    };

    window.addEventListener('errandPaymentMarked', handlePaymentMarked);
    window.addEventListener('errandCompleted', handleErrandCompleted);

    return () => {
      window.removeEventListener('errandPaymentMarked', handlePaymentMarked);
      window.removeEventListener('errandCompleted', handleErrandCompleted);
    };
  }, [throttledRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-BW', {
      style: 'currency',
      currency: 'BWP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-BW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <button
            onClick={fetchDriverData}
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading && earningsSummary.total_earnings === 0 && driverErrands.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <DashboardStatsSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 overflow-x-hidden">
      {/* Removed duplicate header - LayoutDash already provides header */}

      <PullToRefresh onRefresh={fetchDriverData}>
        {/* Mobile-Optimized Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {/* Mobile-First Notifications */}
        {isVerified && (
          <div className="mb-3 flex items-center justify-center sm:justify-start">
            <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center space-x-2 shadow-sm">
              <span className="text-green-600 text-base">✓</span>
              <span className="text-green-800 font-medium text-sm">Verified Driver</span>
            </div>
          </div>
        )}

        {/* Mobile-Optimized KYC Notification */}
        {kycNotification.show && (
          <div className="mb-4 flex items-center justify-center sm:justify-start px-2 sm:px-0">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 flex items-start space-x-3 max-w-2xl w-full shadow-sm">
              <div className="flex-shrink-0 pt-0.5">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-yellow-800 font-semibold text-sm leading-tight">Verification Required</p>
                <p className="text-yellow-700 text-sm mt-1 leading-relaxed">{kycNotification.message}</p>
                {kycNotification.actionText && kycNotification.onAction && (
                  <button
                    onClick={kycNotification.onAction}
                    className="mt-3 bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-700 transition-all duration-200 active:scale-95 touch-manipulation"
                  >
                    {kycNotification.actionText}
                  </button>
                )}
              </div>
              <button
                onClick={() => setKycNotification({ show: false, message: '' })}
                className="flex-shrink-0 text-yellow-600 hover:text-yellow-800 p-1 touch-manipulation"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 mb-6 overflow-x-auto">
          <div className="flex space-x-1 p-1 min-w-max sm:min-w-0">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'errands', label: 'My Errands', icon: '📦' },
              { id: 'earnings', label: 'Earnings', icon: '💰' },
              { id: 'commission', label: 'Commission', icon: '💵' },
              { id: 'analytics', label: 'Analytics', icon: '📈' },
              { id: 'charges', label: 'Extra Charges', icon: '💳' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 sm:flex-none py-3 px-2 sm:px-4 rounded-lg font-medium transition-colors text-sm sm:text-base whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50'
                }`}
              >
                <span className="mr-1 sm:mr-2">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'commission' && (
          <div className="space-y-6">
            {/* Commission Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">Pending Commission</h3>
                <p className="text-3xl font-bold text-orange-600">{formatCurrency(earningsSummary.pending_commission)}</p>
                <p className="text-sm text-neutral-500 mt-2">Available for payout</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">Total Earned</h3>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(earningsSummary.total_earnings)}</p>
                <p className="text-sm text-neutral-500 mt-2">Lifetime earnings</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">Commission Rate</h3>
                <p className="text-3xl font-bold text-blue-600">{(earningsSummary.commission_rate * 100).toFixed(1)}%</p>
                <p className="text-sm text-neutral-500 mt-2">Platform commission</p>
              </div>
            </div>

            {/* Commission Payment Form */}
            {earningsSummary.pending_commission > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">Request Commission Payment</h3>
                <form onSubmit={handleCommissionPayment} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Payment Method *
                      </label>
                      <select
                        name="payment_method"
                        value={commissionForm.payment_method}
                        onChange={(e) => setCommissionForm(prev => ({ ...prev, payment_method: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select payment method</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="mobile_money">Mobile Money</option>
                        <option value="cash_pickup">Cash Pickup</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Amount *
                      </label>
                      <input
                        type="number"
                        name="amount"
                        value={commissionForm.amount}
                        onChange={(e) => setCommissionForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                        min="0"
                        max={earningsSummary.pending_commission}
                        step="0.01"
                        placeholder="Enter amount"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Max: {formatCurrency(earningsSummary.pending_commission)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Account Details
                    </label>
                    <textarea
                      name="account_details"
                      value={commissionForm.account_details}
                      onChange={(e) => setCommissionForm(prev => ({ ...prev, account_details: e.target.value }))}
                      placeholder="Provide your account details (bank account number, mobile money number, etc.)"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={commissionLoading}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    {commissionLoading ? 'Submitting Request...' : 'Request Payment'}
                  </button>
                </form>
              </div>
            )}

            {/* Commission Payment History */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-800">Payment History</h3>
              </div>
              <div className="p-4 sm:p-6">
                {commissionHistory.length > 0 ? (
                  <div className="space-y-3">
                    {commissionHistory.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                              payment.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                              payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {payment.status}
                            </span>
                            <span className="text-sm font-medium text-neutral-700 capitalize">
                              {payment.payment_method.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500">
                            {formatDate(payment.created_at)}
                          </p>
                          {payment.account_details && (
                            <p className="text-xs text-neutral-600 mt-1">
                              {payment.account_details}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">{formatCurrency(payment.amount)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-neutral-500 text-center py-4">No payment history yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 truncate">Total Earnings</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600 truncate">{formatCurrency(earningsSummary.total_earnings)}</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                    <span className="text-green-600 text-lg sm:text-xl">💰</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 truncate">Completed Errands</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600 truncate">{earningsSummary.completed_errands}</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                    <span className="text-blue-600 text-lg sm:text-xl">📦</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 truncate">Completion Rate</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600 truncate">{driverStats.completion_rate.toFixed(1)}%</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                    <span className="text-purple-600 text-lg sm:text-xl">📈</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 truncate">Pending Commission</p>
                    <p className="text-xl sm:text-2xl font-bold text-orange-600 truncate">{formatCurrency(earningsSummary.pending_commission)}</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                    <span className="text-orange-600 text-lg sm:text-xl">⏳</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Errands */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-neutral-200">
                <h2 className="text-base sm:text-lg font-semibold text-neutral-800">Recent Errands</h2>
              </div>
              <div className="p-4 sm:p-6">
                {driverErrands.slice(0, 5).length > 0 ? (
                  <div className="space-y-4">
                    {driverErrands.slice(0, 5).map((errand) => (
                      <div key={errand.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-neutral-50 rounded-lg gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-neutral-800 truncate">{errand.title}</h3>
                          <p className="text-sm text-neutral-600 truncate">Customer • {errand.pickup_location}</p>
                          <p className="text-xs text-neutral-500">{formatDate(errand.created_at)}</p>
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                          <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(errand.status)}`}>
                            {errand.status.replace('_', ' ')}
                          </span>
                          <span className="font-semibold text-green-600 whitespace-nowrap">{formatCurrency(errand.total_cost || errand.estimated_value || 0)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-neutral-500">No errands yet. Start by accepting available errands!</p>
                    <button
                      onClick={() => navigate('/driver/errands')}
                      className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      View Available Errands
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'errands' && (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-semibold text-neutral-800">My Errands</h2>
                <button
                  onClick={() => navigate('/errands')}
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  View All →
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              {driverErrands.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {driverErrands.slice(0, 3).map((errand) => (
                    <div key={errand.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200 hover:bg-neutral-100 transition-colors">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-neutral-800 truncate text-sm">{errand.title}</h3>
                        <p className="text-xs text-neutral-600 truncate">{errand.pickup_location}</p>
                        <p className="text-xs text-neutral-500">{formatDate(errand.created_at)}</p>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(errand.status)}`}>
                          {errand.status.replace('_', ' ')}
                        </span>
                        <span className="font-semibold text-green-600 whitespace-nowrap text-sm">{formatCurrency(errand.total_cost || errand.estimated_value || 0)}</span>
                      </div>
                    </div>
                  ))}
                  {driverErrands.length > 3 && (
                    <div className="text-center pt-2">
                      <button
                        onClick={() => navigate('/errands')}
                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                      >
                        View {driverErrands.length - 3} more errands →
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-neutral-500 text-sm">No errands yet. Start by accepting available errands!</p>
                  <button
                    onClick={() => navigate('/driver/errands')}
                    className="mt-3 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    View Available Errands
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="space-y-6">
            {/* Earnings Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">Total Earnings</h3>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(earningsSummary.total_earnings)}</p>
                <p className="text-sm text-neutral-500 mt-2">All time earnings</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">This Month</h3>
                <p className="text-3xl font-bold text-blue-600">{formatCurrency(earningsSummary.monthly_earnings)}</p>
                <p className="text-sm text-neutral-500 mt-2">Current month earnings</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">Commission Due</h3>
                <p className="text-3xl font-bold text-orange-600">{formatCurrency(earningsSummary.pending_commission)}</p>
                <p className="text-sm text-neutral-500 mt-2">Pending platform commission</p>
              </div>
            </div>

            {/* Commission Info */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
              <h3 className="text-lg font-semibold text-neutral-800 mb-4">Commission Structure</h3>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-neutral-700">
                  <span className="font-semibold">Platform Commission:</span> {(earningsSummary.commission_rate * 100).toFixed(1)}% of each errand
                </p>
                <p className="text-sm text-neutral-600 mt-2">
                  Commission is automatically calculated and deducted from your earnings.
                  You can view detailed breakdown in the analytics section.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {analyticsLoading && !driverAnalytics && (
              <AnalyticsSkeleton />
            )}

            {driverAnalytics && (
              <>
                {/* Real-time Status Indicator */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="ml-2 text-green-800 font-medium">Live Analytics</span>
                    </div>
                    <span className="text-sm text-green-600">Updates every 30 seconds</span>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                    <h3 className="text-sm font-medium text-neutral-600">Total Distance</h3>
                    <p className="text-2xl font-bold text-blue-600">{driverAnalytics.driver.total_distance.toFixed(1)} km</p>
                    <p className="text-xs text-neutral-500 mt-1">Lifetime distance driven</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                    <h3 className="text-sm font-medium text-neutral-600">Average Rating</h3>
                    <p className="text-2xl font-bold text-yellow-600">{driverAnalytics.driver.rating.toFixed(1)} ⭐</p>
                    <p className="text-xs text-neutral-500 mt-1">Customer satisfaction</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                    <h3 className="text-sm font-medium text-neutral-600">Completion Rate</h3>
                    <p className="text-2xl font-bold text-green-600">{driverAnalytics.performance.completion_rate}%</p>
                    <p className="text-xs text-neutral-500 mt-1">{driverAnalytics.performance.completed_errands} of {driverAnalytics.performance.total_errands} errands</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                    <h3 className="text-sm font-medium text-neutral-600">Avg Response Time</h3>
                    <p className="text-2xl font-bold text-purple-600">{driverAnalytics.driver.response_time_avg} min</p>
                    <p className="text-xs text-neutral-500 mt-1">Time to accept errands</p>
                  </div>
                </div>

                {/* Earnings Analytics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Today's Earnings</h3>
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(driverAnalytics.earnings.today)}</p>
                    <p className="text-sm text-neutral-500 mt-2">Earnings from today</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Net Earnings ({driverAnalytics.earnings.period})</h3>
                    <p className="text-3xl font-bold text-blue-600">{formatCurrency(driverAnalytics.earnings.net)}</p>
                    <p className="text-sm text-neutral-500 mt-2">After commission deduction</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Active Errands</h3>
                    <p className="text-3xl font-bold text-orange-600">{driverAnalytics.performance.active_errands}</p>
                    <p className="text-sm text-neutral-500 mt-2">Currently in progress</p>
                  </div>
                </div>

                {/* Earnings Trend Chart */}
                <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-4">Earnings Trend (Last 6 Months)</h3>
                  <div className="space-y-3">
                    {driverAnalytics.earnings_by_month.map((month, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-neutral-700 w-20">{month.month}</span>
                        <div className="flex-1 mx-4">
                          <div className="w-full bg-neutral-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{
                                width: `${Math.min((month.net / Math.max(...driverAnalytics.earnings_by_month.map(m => m.net))) * 100, 100)}%`
                              }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-green-600 w-16 text-right">{formatCurrency(month.net)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Errand Status Distribution */}
                <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-4">Errand Status Distribution</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(driverAnalytics.errands_by_status).map(([status, count]) => (
                      <div key={status} className="text-center p-4 bg-neutral-50 rounded-lg">
                        <div className="text-2xl font-bold text-neutral-900">{count}</div>
                        <div className="text-sm text-neutral-600 capitalize">{status.replace('_', ' ')}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {driverAnalytics.recent_activity.slice(0, 5).map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                              {activity.status.replace('_', ' ')}
                            </span>
                            <span className="text-sm text-neutral-600">
                              {activity.pickup_location} {activity.dropoff_location ? `→ ${activity.dropoff_location}` : ''}
                            </span>
                          </div>
                          <div className="text-xs text-neutral-500 mt-1">
                            {formatDate(activity.created_at)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-green-600">{formatCurrency(activity.amount)}</div>
                          <div className="text-xs text-neutral-500">Commission: {formatCurrency(activity.commission)}</div>
                        </div>
                      </div>
                    ))}
                    {driverAnalytics.recent_activity.length === 0 && (
                      <p className="text-neutral-500 text-center py-4">No recent activity</p>
                    )}
                  </div>
                </div>

                {/* Performance Insights */}
                <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-4">Performance Insights</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-neutral-800 mb-2">Completion Time</h4>
                      <p className="text-2xl font-bold text-blue-600">{driverAnalytics.performance.average_completion_time.toFixed(1)}h</p>
                      <p className="text-sm text-neutral-600">Average time to complete errands</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-800 mb-2">Success Rate</h4>
                      <p className="text-2xl font-bold text-green-600">
                        {driverAnalytics.performance.total_errands > 0
                          ? ((driverAnalytics.performance.completed_errands / driverAnalytics.performance.total_errands) * 100).toFixed(1)
                          : 0}%
                      </p>
                      <p className="text-sm text-neutral-600">Errands completed successfully</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!driverAnalytics && !analyticsLoading && (
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
                <div className="text-center py-8">
                  <p className="text-neutral-500">Unable to load analytics data. Please try again later.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'charges' && (
          <ExtraChargesPanel />
        )}
        </div>
      </PullToRefresh>

      {selectedErrandForRating && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          errandId={selectedErrandForRating.id}
          rateeId={selectedErrandForRating.user_id!}
          rateeName={selectedErrandForRating.profiles?.full_name || 'Customer'}
          ratingType="driver_to_customer"
          onRatingSubmitted={() => {
            setShowRatingModal(false);
            fetchDriverErrands();
          }}
        />
      )}
    </div>
  );
};

export default DriverDashboard;
